import type { OrderListDto } from "@/types/order.dto";
import type { ProductListDto, ProductResponseDto } from "@/types/product.dto";
import type { SettlementResponseDto } from "@/types/wallet.dto";
import type { StoreOrderRow, StoreProductRow, StoreEarningRow } from "@/types/store.ui";
import { DeliveryType, OrderStatus, PaymentMethod, PaymentStatus, ProductStatus, SettlementStatus } from "@/types/enums";
import { resolveUploadUrl } from "@/services/filesService";
import type { ProductFormData } from "@/types/product";
import type { ProductCreateDto, ProductUpdateDto } from "@/types/product.dto";
import { formatOrderDate, mapPaymentMethodToUi } from "./order-mappers";

const DEFAULT_PRODUCT_IMAGE = "";

export function mapOrderStatusToStoreUi(status: OrderStatus): StoreOrderRow["status"] {
  switch (status) {
    case OrderStatus.Pending:
      return "new";
    case OrderStatus.Confirmed:
      return "confirmed";
    case OrderStatus.Processing:
      return "preparing";
    case OrderStatus.ReadyForPickup:
      return "ready";
    case OrderStatus.OutForDelivery:
      return "dispatched";
    case OrderStatus.Delivered:
      return "delivered";
    case OrderStatus.Cancelled:
      return "cancelled";
    default:
      return "new";
  }
}

export function mapDeliveryTypeToStoreUi(type: DeliveryType): StoreOrderRow["deliveryType"] {
  return type === DeliveryType.StoreDelivery ? "self" : "platform";
}

export function mapOrderListDtoToStoreOrder(dto: OrderListDto): StoreOrderRow {
  return {
    id: String(dto.id),
    orderId: dto.orderNumber,
    customerName: dto.customerName,
    customerMemberId: dto.customerMemberId,
    customerPhone: dto.customerPhone,
    items: [],
    itemCount: dto.itemCount,
    total: dto.totalAmount,
    status: mapOrderStatusToStoreUi(dto.status),
    deliveryType: mapDeliveryTypeToStoreUi(dto.deliveryType),
    orderDate: formatOrderDate(dto.createdAt),
    createdAtRaw: dto.createdAt,
    paymentMethod: mapPaymentMethodToUi(dto.paymentMethod),
    paymentStatus: dto.paymentStatus,
    paymentStatusText: dto.paymentStatusText,
  };
}

export function mapProductStatusToStoreUi(
  status: ProductStatus,
  inStock: boolean,
): StoreProductRow["status"] {
  if (status === ProductStatus.Rejected) return "rejected";
  if (status === ProductStatus.NeedsRevision) return "needs_revision";
  if (status === ProductStatus.Pending) return "pending";
  if (!inStock || status === ProductStatus.OutOfStock) return "out_of_stock";
  return "active";
}

function effectiveSellingPrice(sellingPrice: number, mrp: number): number {
  return sellingPrice > 0 ? sellingPrice : mrp;
}

export function mapProductListDtoToStoreProduct(dto: ProductListDto, stock?: number | null): StoreProductRow {
  const price = effectiveSellingPrice(dto.sellingPrice, dto.mrp);
  return {
    id: String(dto.id),
    name: dto.name,
    category: dto.categoryName,
    price,
    originalPrice: dto.mrp > price ? dto.mrp : undefined,
    businessVolume: dto.businessVolume,
    stock: dto.stockQuantity ?? (stock ?? null),
    status: mapProductStatusToStoreUi(dto.status, dto.inStock),
    image: resolveUploadUrl(dto.primaryImage ?? DEFAULT_PRODUCT_IMAGE),
    sold: 0,
    rating: dto.rating,
    sourceProductId: dto.sourceProductId,
    isAdminProduct: Boolean(dto.sourceProductId || dto.isAdminProduct),
  };
}

export function mapProductResponseDtoToStoreProduct(dto: ProductResponseDto): StoreProductRow {
  const price = effectiveSellingPrice(dto.sellingPrice, dto.mrp);
  return {
    id: String(dto.id),
    name: dto.name,
    category: dto.categoryName,
    price,
    originalPrice: dto.mrp > price ? dto.mrp : undefined,
    stock: dto.stockQuantity,
    status: mapProductStatusToStoreUi(dto.status, dto.inStock),
    image: resolveUploadUrl(dto.primaryImage ?? dto.images?.[0]?.imageUrl ?? DEFAULT_PRODUCT_IMAGE),
    sold: 0,
    rating: dto.rating,
    sourceProductId: dto.sourceProductId,
    isAdminProduct: Boolean(dto.sourceProductId || dto.isAdminProduct),
  };
}

export function mapSettlementToStoreEarning(dto: SettlementResponseDto): StoreEarningRow {
  const statusMap: Record<SettlementStatus, StoreEarningRow["status"]> = {
    [SettlementStatus.Pending]: "pending",
    [SettlementStatus.Processing]: "processing",
    [SettlementStatus.Completed]: "paid",
    [SettlementStatus.Failed]: "pending",
  };

  return {
    id: String(dto.id),
    period: `${formatOrderDate(dto.periodStart)} – ${formatOrderDate(dto.periodEnd)}`,
    totalSales: dto.amount,
    commission: 0,
    netEarning: dto.amount,
    orders: dto.orderCount,
    status: statusMap[dto.status] ?? "pending",
    paidDate: dto.processedAt ? formatOrderDate(dto.processedAt) : undefined,
  };
}

export function formDataToProductCreateDto(data: ProductFormData): ProductCreateDto {
  const images = (data.imageUrls?.length ? data.imageUrls : data.imageUrl ? [data.imageUrl] : []).map(
    (url, index) => ({
      imageUrl: url,
      isPrimary: index === 0,
      displayOrder: index,
    }),
  );

  return {
    categoryId: Number(data.categoryId),
    name: data.name,
    description: data.description,
    mrp: data.mrp,
    // Store create: admin ladder prices default to 0 (admin can set later).
    distributorPrice: data.dp || 0,
    gstPercent: data.gstPercent || 0,
    wholesalePrice: data.wholesalePrice || 0,
    retailPrice: data.retailPrice || 0,
    // Form edits C.P (customerPrice); sellingPrice is the API field and may stay at default 0.
    sellingPrice: data.customerPrice || data.sellingPrice || data.mrp,
    deliveryCharge: data.deliveryCharge || 0,
    distributorCommissionPercent: data.distributorCommissionPercent || 0,
    wholesaleCommissionPercent: data.wholesaleCommissionPercent || 0,
    retailCommissionPercent: data.retailCommissionPercent || 0,
    businessVolume: data.businessVolume,
    stockQuantity: data.stockQuantity,
    lowStockThreshold: 5,
    sku: data.sku || undefined,
    unit: data.unit,
    isVeg: data.isVeg,
    tags: data.tags || undefined,
    images: images.length ? images : undefined,
  };
}

export function formDataToProductUpdateDto(
  data: ProductFormData,
  options?: { includeAdminPricing?: boolean; isActive?: boolean },
): ProductUpdateDto {
  const created = formDataToProductCreateDto(data);
  const isActive = options?.isActive ?? true;

  if (options?.includeAdminPricing) {
    return {
      ...created,
      isActive,
    };
  }

  // Omit DP/WP/RP on store updates so existing admin ladder values are preserved server-side.
  const {
    distributorPrice: _dp,
    wholesalePrice: _wp,
    retailPrice: _rp,
    distributorCommissionPercent: _dc,
    wholesaleCommissionPercent: _wc,
    retailCommissionPercent: _rc,
    ...rest
  } = created;
  return {
    ...rest,
    isActive,
  };
}

export function productResponseToFormData(dto: ProductResponseDto): ProductFormData {
  return {
    name: dto.name,
    description: dto.description ?? "",
    categoryId: String(dto.categoryId),
    mrp: dto.mrp,
    dp: dto.distributorPrice ?? 0,
    gstPercent: dto.gstPercent ?? 0,
    wholesalePrice: dto.wholesalePrice ?? 0,
    retailPrice: dto.retailPrice ?? 0,
    customerPrice: dto.sellingPrice,
    deliveryCharge: dto.deliveryCharge ?? 0,
    distributorCommissionPercent: dto.distributorCommissionPercent ?? 0,
    wholesaleCommissionPercent: dto.wholesaleCommissionPercent ?? 0,
    retailCommissionPercent: dto.retailCommissionPercent ?? 0,
    businessVolume: dto.businessVolume ?? Math.round(dto.sellingPrice * 0.1),
    sellingPrice: dto.sellingPrice,
    stockQuantity: dto.stockQuantity,
    unit: dto.unit,
    sku: dto.sku ?? "",
    isVeg: dto.isVeg,
    tags: dto.tags ?? "",
    imageUrl: resolveUploadUrl(dto.primaryImage ?? dto.images?.[0]?.imageUrl ?? DEFAULT_PRODUCT_IMAGE),
    imageUrls: dto.images?.map((i) => resolveUploadUrl(i.imageUrl)) ?? (dto.primaryImage ? [resolveUploadUrl(dto.primaryImage)] : []),
  };
}

export function mapOrderListDtosToStoreOrders(items: OrderListDto[]): StoreOrderRow[] {
  return items.map(mapOrderListDtoToStoreOrder);
}

export function mapProductListDtosToStoreProducts(items: ProductListDto[]): StoreProductRow[] {
  return items.map((item) => mapProductListDtoToStoreProduct(item));
}

export function mapSettlementsToStoreEarnings(items: SettlementResponseDto[]): StoreEarningRow[] {
  return items.map(mapSettlementToStoreEarning);
}

export function getStoreOrderAdvanceAction(status: StoreOrderRow["status"]): string | null {
  const labels: Record<string, string> = {
    new: "Accept",
    confirmed: "Start Preparing",
    preparing: "Mark Ready",
    ready: "Dispatch",
    dispatched: "Mark Delivered",
  };
  return labels[status] ?? null;
}

export function getNextStoreOrderStatus(status: StoreOrderRow["status"]): StoreOrderRow["status"] | null {
  const next: Partial<Record<StoreOrderRow["status"], StoreOrderRow["status"]>> = {
    new: "confirmed",
    confirmed: "preparing",
    preparing: "ready",
    ready: "dispatched",
    dispatched: "delivered",
  };
  return next[status] ?? null;
}
